import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getToken, getUserDataFromLocal, removeUserDataFromLocal, saveUserDataToLocal } from "../common/utils";
import {
    changePassword,
    forgotPassword as forgotPasswordApi,
    verifyResetToken as verifyResetTokenApi,
    resetPassword as resetPasswordApi,
    login,
} from "../apis/apiHandler";

export const loginUser = createAsyncThunk(
    'auth/loginUser',
    async ({ payload }, { rejectWithValue }) => {
        try {
            const response = await login(payload);
            if (response?.code === 200) {
                saveUserDataToLocal(response.data);
                return response;
            } else {
                return rejectWithValue(response);
            }
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
)

export const updatePassword = createAsyncThunk(
    'auth/password',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await changePassword(payload);
            if (response?.code === 200) {
                return response;
            } else {
                return rejectWithValue(response);
            }
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
)

export const forgotPassword = createAsyncThunk(
    'auth/forgotPassword',
    async ({ payload }, { rejectWithValue }) => {
        try {
            const response = await forgotPasswordApi(payload);
            if (response?.code === 200) {
                return response;
            } else {
                return rejectWithValue(response);
            }
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
)

export const verifyResetToken = createAsyncThunk(
    'auth/verifyResetToken',
    async ({ token }, { rejectWithValue }) => {
        try {
            const response = await verifyResetTokenApi({ token });
            if (response?.code === 200) {
                return response;
            } else {
                return rejectWithValue(response);
            }
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
)

export const resetPassword = createAsyncThunk(
    'auth/resetPassword',
    async ({ payload }, { rejectWithValue }) => {
        try {
            const response = await resetPasswordApi(payload);
            if (response?.code === 200) {
                return response;
            } else {
                return rejectWithValue(response);
            }
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
)

const userToken = getToken();

const initialState = {
    loading: false,
    userInfo: getUserDataFromLocal(),
    userToken,
    error: null,
    success: false,
    changePasswordSuccess: false,
    forgotPasswordSuccess: false,
    tokenVerified: false,
    resetPasswordSuccess: false,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: (state) => {
            removeUserDataFromLocal();
            state.loading = false;
            state.userInfo = null;
            state.userToken = null;
            state.error = null;
        },
        resetChangePasswordState: (state) => {
            state.changePasswordSuccess = false;
            state.error = null;
        },
        resetForgotPasswordState: (state) => {
            state.forgotPasswordSuccess = false;
            state.error = null;
        },
        resetPasswordState: (state) => {
            state.resetPasswordSuccess = false;
            state.tokenVerified = false;
            state.error = null;
        },
    },

    extraReducers: (builder) => {
        builder
            // ── Login ──
            .addCase(loginUser.pending, (state) => {
                state.loading = true;
            })
            .addCase(loginUser.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.userInfo = payload?.data;
                state.userToken = payload?.data?.admin_device_info?.token;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })

            // ── Change Password ──
            .addCase(updatePassword.pending, (state) => {
                state.loading = true;
                state.changePasswordSuccess = false;
            })
            .addCase(updatePassword.fulfilled, (state) => {
                state.loading = false;
                state.changePasswordSuccess = true;
            })
            .addCase(updatePassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.changePasswordSuccess = false;
            })

            // ── Forgot Password ──
            .addCase(forgotPassword.pending, (state) => {
                state.loading = true;
                state.forgotPasswordSuccess = false;
                state.error = null;
            })
            .addCase(forgotPassword.fulfilled, (state) => {
                state.loading = false;
                state.forgotPasswordSuccess = true;
            })
            .addCase(forgotPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.forgotPasswordSuccess = false;
            })

            // ── Verify Reset Token ──
            .addCase(verifyResetToken.pending, (state) => {
                state.loading = true;
                state.tokenVerified = false;
                state.error = null;
            })
            .addCase(verifyResetToken.fulfilled, (state) => {
                state.loading = false;
                state.tokenVerified = true;
            })
            .addCase(verifyResetToken.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.tokenVerified = false;
            })

            // ── Reset Password ──
            .addCase(resetPassword.pending, (state) => {
                state.loading = true;
                state.resetPasswordSuccess = false;
                state.error = null;
            })
            .addCase(resetPassword.fulfilled, (state) => {
                state.loading = false;
                state.resetPasswordSuccess = true;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.resetPasswordSuccess = false;
            })
    }
})

export const { logout, resetChangePasswordState, resetForgotPasswordState, resetPasswordState } = authSlice.actions;

export default authSlice.reducer;